// src/components/SubscriptionManagement/TransactionDetails.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Stack,
} from '@mui/material';
import { Visibility, ContentCopy } from '@mui/icons-material';
import { ColorsConstants } from '../../cssUtils/constants.js';
import { GET_TRANSACTIONS_LIST } from '../../config/urls.js';
import { getCurrencySymbol } from './utils.js';
import typography from '../../cssUtils/typography.js';
import AppDropdown from '../../common/app-dropdown.jsx';
import AppTable from '../../common/app-table.jsx';
import BackButton from '../../common/back-button.jsx';
import { formatWithTzLabel } from '../../utils/dateUtils';



const TransactionDetails = ({ onBack, billingCurrency = "INR" }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [timeFilter, setTimeFilter] = useState('last30days');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const pageSize = 20;

  // 1. Fetch Transactions with filters and pagination
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const tenantId = localStorage.getItem('tenant');
        const token = localStorage.getItem('token');

        // Build query params
        const params = new URLSearchParams({
          page: page,
          page_size: pageSize,
        });

        // Add status filter if not 'all'
        if (statusFilter !== 'all') {
          params.append('status', statusFilter);
        }

        // Add time filter
        const dateFilter = getDateFilter(timeFilter);
        if (dateFilter) {
          params.append('initiated_at_after', dateFilter.after);
          if (dateFilter.before) {
            params.append('initiated_at_before', dateFilter.before);
          }
        }

        const response = await axios.get(`${GET_TRANSACTIONS_LIST}?${params.toString()}`, {
          headers: {
            'X-Tenant-ID': tenantId,
            'Authorization': `Bearer ${token}`
          }
        });

        // Handle paginated response
        const data = response.data;
        setTransactions(data.results || []);
        setTotalCount(data.count || 0);
        setNextPage(data.next);
        setPrevPage(data.previous);

      } catch (err) {
        console.error("❌ Failed to fetch transactions:", err);
        setError("Failed to load transaction history.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [page, timeFilter, statusFilter]);

  // Helper function to get date filters based on time range
  const getDateFilter = (filter) => {
    const today = new Date();
    const after = new Date();
    
    switch(filter) {
      case 'last7days':
        after.setDate(today.getDate() - 7);
        return {
          after: after.toISOString().split('T')[0]
        };
      case 'last30days':
        after.setDate(today.getDate() - 30);
        return {
          after: after.toISOString().split('T')[0]
        };
      case 'last90days':
        after.setDate(today.getDate() - 90);
        return {
          after: after.toISOString().split('T')[0]
        };
      case 'all':
      default:
        return null;
    }
  };

  // 2. Handle View Transaction Details
  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
  };

  // 3. Copy to Clipboard
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You can add a snackbar notification here
  };

  // 4. Status Chip Component - Using status_display from backend
  const getStatusChip = (transaction) => {
    const status = transaction.status || 'pending';
    const statusDisplay = transaction.status_display || status;
    
    const configs = {
      success: { label: 'Success', color: '#c6f6d5', textColor: '#22543d' },
      pending: { label: 'Pending', color: '#fef5e7', textColor: '#975a16' },
      failed: { label: 'Failed', color: '#fed7d7', textColor: '#742a2a' },
      initiated: { label: 'Initiated', color: '#e3f2fd', textColor: '#1565c0' },
      cancelled: { label: 'Cancelled', color: '#f3e5f5', textColor: '#6a1b9a' },
      refunded: { label: 'Refunded', color: '#fff9c4', textColor: '#f57f17' },
      partially_refunded: { label: 'Partial Refund', color: '#fff9c4', textColor: '#f57f17' },
    };
    
    const config = configs[status] || { 
      label: statusDisplay, 
      color: '#e3f2fd', 
      textColor: '#1565c0' 
    };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{ 
          backgroundColor: config.color, 
          color: config.textColor, 
          fontWeight: 600, 
          fontSize: '0.75rem' 
        }}
      />
    );
  };

  // 5. Transaction Type Badge - Using transaction_type_display from backend
  const getTransactionTypeBadge = (transaction) => {
    const type = transaction.transaction_type || '';
    const displayName = transaction.transaction_type_display || type;
    
    return (
      <Chip
        label={displayName}
        size="small"
        sx={{
          backgroundColor: ColorsConstants.primaryBlue60,
          color: ColorsConstants.primaryGray70,
          fontSize: '0.7rem',
          fontWeight: 600,
        }}
      />
    );
  };

  // 6. Format Date
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // 7. Format Date with Time
  const formatDateTime = (dateString) => {
    if (!dateString) return '--';
    return formatWithTzLabel(dateString, 'MMM DD, YYYY, hh:mm A');
  };

  // 8. Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleCustomPageChange = (event, newPage) => {
    handlePageChange(event, newPage + 1); // 0-based → 1-based
  };

  if (loading) {
    return (
      <Card sx={{ border: `1px solid ${ColorsConstants.primaryGray10}`, p: 3 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={300} />
      </Card>
    );
  }

  const tableColumns = [
    {
      id: "initiated_at",
      label: "Created On",
      render: (transaction) => (
        <span style={{ color: ColorsConstants.primaryGray70 }}>
          {formatWithTzLabel(transaction.initiated_at)}
        </span>
      ),
    },
    {
      id: "external_payment_id",
      label: "Payment ID",
      render: (transaction) => (
        <span style={{ color: ColorsConstants.primaryGray70, fontFamily: "monospace", fontSize: "0.85rem" }}>
          {transaction.external_payment_id || transaction.cashfree_payment_id || "--"}
        </span>
      ),
    },
    {
      id: "type",
      label: "Type",
      render: (transaction) => getTransactionTypeBadge(transaction),
    },
    {
      id: "amount",
      label: "Amount",
      render: (transaction) => (
        <span style={{ color: ColorsConstants.primaryBackgroundColor, fontWeight: 600 }}>
          {getCurrencySymbol(billingCurrency)}{parseFloat(transaction.amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      id: "payment_method",
      label: "Payment Method",
      render: (transaction) => (
        <span style={{ color: ColorsConstants.primaryGray70, textTransform: "uppercase", fontSize: "0.85rem" }}>
          {transaction.payment_method_display || transaction.payment_method || "--"}
        </span>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (transaction) => getStatusChip(transaction),
    },
    {
      id: "__actions__",
      label: "Actions",
      align: "center",
      render: (transaction) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewTransaction(transaction);
            }}
            sx={{ color: ColorsConstants.primaryBlue100 }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];
  return (
    <>
      {onBack && (
        <Box sx={{ mb: 2 }}>
          <BackButton onClick={onBack} />
        </Box>
      )}
      <Card sx={{ border: `1px solid ${ColorsConstants.primaryGray10}` }}>
        <Box sx={{ p: 3 }}>
          {/* Header with Filters */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryBackgroundColor }}>
                Transaction Details
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={[
                  { label: "Last 7 days", value: "last7days" },
                  { label: "Last 30 days", value: "last30days" },
                  { label: "Last 90 days", value: "last90days" },
                  { label: "All Time", value: "all" },
                ]}
                selectedValue={timeFilter}
                onMenuSelect={(value) => {
                  setTimeFilter(value);
                  setPage(1);
                }}
              >
                {timeFilter
                  ? { last7days: "Last 7 days", last30days: "Last 30 days", last90days: "Last 90 days", all: "All Time" }[timeFilter]
                  : "Time Period"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={[
                  { label: "All Status", value: "all" },
                  { label: "Success", value: "success" },
                  { label: "Failed", value: "failed" },
                  { label: "Pending", value: "pending" },
                  { label: "Initiated", value: "initiated" },
                ]}
                selectedValue={statusFilter}
                onMenuSelect={(value) => {
                  setStatusFilter(value);
                  setPage(1);
                }}
              >
                {statusFilter
                  ? { all: "All Status", success: "Success", failed: "Failed", pending: "Pending", initiated: "Initiated" }[statusFilter]
                  : "Status"}
              </AppDropdown>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {transactions.length === 0 ? (
            <Typography sx={{ color: ColorsConstants.primaryGray70, textAlign: 'center', py: 5 }}>
              No transactions found.
            </Typography>
          ) : (
            <>
              
            <AppTable
              columns={tableColumns}
              rows={transactions}
              rowKey={(transaction, index) => transaction.id ?? index}
              emptyMessage="No transactions found"
              // Blue header matching primaryBackgroundColor
              headCellSx={{
                backgroundColor: ColorsConstants.primaryBackgroundColor,
                color: "white",
                fontWeight: 600,
                borderBottom: "none",
              }}
              // No alternating rows — original had flat white + hover only
              rowSx={() => ({
                backgroundColor: "#ffffff",
                "&:hover": { backgroundColor: ColorsConstants.primaryBlue60 },
                cursor: "pointer",
              })}
              paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
              stickyHeader={false}
              hideMainScroll={false}
              onRowClick={(transaction) => handleViewTransaction(transaction)}
              rowClickable
              // CustomPagination — only shown when totalCount > pageSize (matches original)
              {...(totalCount > pageSize && {
                count: totalCount,
                page: page - 1,                    // 1-based → 0-based
                rowsPerPage: pageSize,
                rowsPerPageOptions: [pageSize],
                onPageChange: handleCustomPageChange,
                onRowsPerPageChange: () => {},
              })}
            />
 
            </>
          )}
        </Box>
      </Card>

      {/* Transaction Detail Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: `1px solid ${ColorsConstants.primaryGray10}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: ColorsConstants.primaryBackgroundColor,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
            Transaction Details
          </Typography>
          <IconButton 
            onClick={handleCloseModal} 
            sx={{ 
              color: 'white', 
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } 
            }}
          >
            ×
          </IconButton>
        </DialogTitle>

        {selectedTransaction && (
          <DialogContent sx={{ p: 3 }}>
            {/* Transaction Overview */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontSize: '15px', fontWeight: 600, color: ColorsConstants.primaryBackgroundColor, mb: 2 }}>
                Transaction Overview
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Payment ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, fontFamily: 'monospace' }}>
                      {selectedTransaction.external_payment_id || '--'}
                    </Typography>
                    {selectedTransaction.external_payment_id && (
                      <Tooltip title="Copy">
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopyToClipboard(selectedTransaction.external_payment_id)}
                          sx={{ color: ColorsConstants.secondaryBackgroundColor }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Cashfree Transaction ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, fontFamily: 'monospace' }}>
                      {selectedTransaction.cashfree_payment_id || '--'}
                    </Typography>
                    {selectedTransaction.cashfree_payment_id && (
                      <Tooltip title="Copy">
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopyToClipboard(selectedTransaction.cashfree_payment_id)}
                          sx={{ color: ColorsConstants.secondaryBackgroundColor }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Transaction Type
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {selectedTransaction.transaction_type_display || selectedTransaction.transaction_type || '--'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Status
                  </Typography>
                  <Box>{getStatusChip(selectedTransaction)}</Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Transaction ID
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedTransaction.id || '--'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Payment Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontSize: '15px', fontWeight: 600, color: ColorsConstants.primaryBackgroundColor, mb: 2 }}>
                Payment Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Amount
                  </Typography>
                  <Typography sx={{ fontSize: '20px', fontWeight: 700, color: ColorsConstants.primaryBackgroundColor }}>
                    {getCurrencySymbol(billingCurrency)}{parseFloat(selectedTransaction.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Payment Method
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, textTransform: 'uppercase' }}>
                    {selectedTransaction.payment_method_display || selectedTransaction.payment_method || '--'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Initiated At
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {formatDateTime(selectedTransaction.initiated_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                    Payment Date
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {formatDateTime(selectedTransaction.payment_date)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Retry Information (if applicable) */}
            {selectedTransaction.is_retry && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontSize: '15px', fontWeight: 600, color: ColorsConstants.primaryBackgroundColor, mb: 2 }}>
                    Retry Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                        Is Retry
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Yes
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                        Retry Count
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Attempt {selectedTransaction.retry_count || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            {/* Failure Information (if failed) */}
            {selectedTransaction.status === 'failed' && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '15px', fontWeight: 600, color: '#742a2a', mb: 2 }}>
                    Failure Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '13px', color: ColorsConstants.primaryGray50, mb: 0.5 }}>
                        Failure Reason
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#742a2a' }}>
                        {selectedTransaction.failure_reason || '--'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default TransactionDetails;